import React from 'react';
import { TagPill, TooltipTrigger } from '@spark';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';
import '@/features/datasets/components/DataCollection/dataOverview/sections/LabelsSection.css';

export const TOOLS_CALLED_TITLE = 'Tools Called Detected';
export const TOOLS_CALLED_EMPTY_VALUE = 'No tool calls detected in data';

// Same threshold as the labels panel: past this many distinct tools the chip
// row switches to the scrollable grid so the box keeps a bounded height.
const MANY_TOOLS_THRESHOLD = 10;

/**
 * Normalize the cached `tool_counts` map (`{ toolName: { total } }`, as returned
 * by `get-label-counts`) into a display list sorted by occurrences (desc), then
 * by tool name so equal counts render in a stable order. Entries without a
 * positive count are dropped; a bare number is tolerated as a legacy shape.
 */
export const buildToolsCalledList = (toolCounts) => {
    if (!toolCounts || typeof toolCounts !== 'object') return [];
    return Object.entries(toolCounts)
        .map(([name, data]) => {
            const raw = typeof data === 'number' ? data : data?.total;
            const count = Number(raw);
            return { name, count: Number.isFinite(count) ? count : 0 };
        })
        .filter((tool) => tool.name && tool.count > 0)
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

const ToolChip = ({ tool }) => (
    <TooltipTrigger text={`${tool.count} ${tool.count === 1 ? 'call' : 'calls'}`} placement="top">
        <span className="data-overview-label-chip">
            <TagPill label={`${tool.name} (${tool.count})`} />
        </span>
    </TooltipTrigger>
);

/**
 * "Tools Called Detected" panel for agentic tasks (autonomy level with tool
 * calling). Mirrors the "Labels Detected" panel visually, but each chip shows
 * the number of times the tool was called across the active (non-archived)
 * records instead of a percentage. Renders nothing for non-agentic tasks and an
 * explicit empty row (like "Code detected") when no tool calls were found.
 */
const ToolsCalledSection = ({ toolCounts, usecase }) => {
    if (!isAgenticAutonomyLevel(usecase?.autonomy_level)) return null;

    const tools = buildToolsCalledList(toolCounts);
    if (tools.length === 0) {
        return (
            <AnalysisPropertyRow
                icon="tool"
                label={TOOLS_CALLED_TITLE}
                value={TOOLS_CALLED_EMPTY_VALUE}
            />
        );
    }

    const hasManyTools = tools.length > MANY_TOOLS_THRESHOLD;
    const chipsClassName = hasManyTools
        ? 'fs-mask labels-grid-container data-overview-labels-chips'
        : 'fs-mask data-overview-labels-chips';

    return (
        <div className="data-overview-labels-panel" data-testid="data-overview-tools-called">
            <div className="data-overview-labels-header">
                <AnalysisPropertyRow
                    icon="tool"
                    label={<span className="fs-unmask">{TOOLS_CALLED_TITLE}</span>}
                    value={<span className="data-overview-labels-count">{tools.length}</span>}
                />
            </div>
            <div className="data-overview-labels-chips-wrap">
                <div className={chipsClassName}>
                    {tools.map((tool) => (
                        <span key={tool.name} className="data-overview-labels-chip-item">
                            <ToolChip tool={tool} />
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ToolsCalledSection;
